import React, {useState} from 'react';
import {useIntl} from 'react-intl';
import {FlexColumn, Modal, Text, TextField} from '@forecast-it/design-system';
import {VERSION_NAME_MAX_LENGTH} from '../shell/NovaWorkflowPromptTab';

export interface NovaSaveAsVersionModalProps {
	// The modal's title: Save as new version by default, or Rename version.
	titleId?: string;
	// The name the field starts with: empty when the author has not typed a new one, or the version's
	// own name when renaming it.
	initialName: string;
	// How many companies the save goes live for, or null when that could not be read. Zero, or none
	// given, says nothing.
	liveCompanyCount?: number | null;
	onSave: (versionName: string) => void;
	onClose: () => void;
}

// Asks for a version name, for Save as new version and for Rename version. Save stays disabled until a
// name is typed; a name of only spaces counts as none.
export function NovaSaveAsVersionModal({
	titleId = 'nova.saveAsVersion.title',
	initialName,
	liveCompanyCount = 0,
	onSave,
	onClose,
}: NovaSaveAsVersionModalProps): JSX.Element {
	const {formatMessage} = useIntl();
	const [name, setName] = useState(initialName);
	const trimmed = name.trim();

	const handleSave = (): void => {
		if (trimmed === '') {
			return;
		}
		onClose();
		onSave(trimmed);
	};

	return (
		<Modal size={Modal.SIZE.SMALL} onCloseModal={onClose}>
			<Modal.Title>{formatMessage({id: titleId})}</Modal.Title>
			<Modal.Content>
				<FlexColumn gap={'m'}>
					<TextField
						label={formatMessage({id: 'nova.prompt.versionNameLabel'})}
						placeholder={formatMessage({id: 'nova.versionNameModal.placeholder'})}
						data-cy="nova-save-as-version-name"
						value={name}
						maxLength={VERSION_NAME_MAX_LENGTH}
						onValueChange={setName}
						size={TextField.SIZE.LARGE}
						width={TextField.WIDTH.FLEX}
					/>
					{liveCompanyCount !== 0 && (
						<Text data-cy="nova-save-as-version-live">
							{liveCompanyCount === null
								? formatMessage({id: 'nova.saveVersion.goesLiveUnknown'})
								: formatMessage({id: 'nova.saveVersion.goesLive'}, {count: liveCompanyCount})}
						</Text>
					)}
				</FlexColumn>
			</Modal.Content>
			<Modal.Footer>
				<Modal.TertiaryFooterButton onPress={onClose} dataCy="nova-save-as-version-cancel">
					{formatMessage({id: 'nova.saveVersion.cancel'})}
				</Modal.TertiaryFooterButton>
				<Modal.PrimaryFooterButton onPress={handleSave} disabled={trimmed === ''} dataCy="nova-save-as-version-confirm">
					{formatMessage({id: 'nova.saveVersion.save'})}
				</Modal.PrimaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
}
